import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { getSlideshowPlaybackDurationMs } from 'ts/common/components/slideshows/slideshowAudioTimeline';
import { getSlideImageUrl } from 'ts/common/components/slideshows/slideshowImagePreloader';
import {
    getSlideLocalProgress,
    getViewportCropPlayback,
    interpolateKeyframes
} from 'ts/common/components/slideshows/slideshowKenBurns';
import {
    buildOverlapTimeline,
    getActiveSlidesAtTime,
    getBoundaryTransitionDurations,
    getSlideDurationMs,
    getSlideTransition,
    resolvePlaybackTime
} from 'ts/common/components/slideshows/slideshowOverlapTimeline';
import {
    SLIDESHOW_DEFAULTS,
    getSlideBackgroundColor
} from 'ts/common/components/slideshows/slideshowUtils';
import { useTranslate } from 'ts/common/hooks';
import { useResizeObserver } from 'ts/common/hooks/useResizeObserver';
import { useSlideshowAudio } from './useSlideshowAudio';

/** Default `getSongStreamUrl` when a caller doesn't wire up music playback — every song stays silent. */
const NO_OP_GET_SONG_STREAM_URL = async (): Promise<string | null> => null;

interface NaturalImageSize {
    width: number;
    height: number;
}

export interface ISlideshowPlayerProps {
    /** Slideshow to display */
    slideshow: SpApi.IEventSlideshow;
    /** Global playback time in milliseconds */
    currentTime: number;
    /** Whether the slideshow is currently playing — also gates music playback. */
    isPlaying?: boolean;
    /** Called when global time changes (optional — supply when wiring scrubbing / controls) */
    onTimeChange?: (timeMs: number) => void;
    /** Called when the highlighted slide index changes */
    onSlideIndexChange?: (index: number) => void;
    /** Transparent PNG watermark overlay URL — rendered above all slide layers */
    watermarkUrl?: string;
    /**
     * When true, renders `loadingIndicator` centered over the visible slideshow
     * content — sized to match the letterboxed/pillarboxed content area itself,
     * not the (possibly wider or taller) outer container.
     */
    isBuffering?: boolean;
    /** Rendered when `isBuffering` is true. */
    loadingIndicator?: React.ReactNode;
    /**
     * Resolves a signed stream URL for a song id on the slideshow's sound timeline. Omit to keep
     * the slideshow silent (e.g. when the caller hasn't wired up an authenticated fetcher).
     */
    getSongStreamUrl?: (songId: string) => Promise<string | null>;
    /** Music playback volume (0–1). Defaults to 1. */
    volume?: number;
    /**
     * Silences the music without stopping it, so the song stays on the slideshow's timeline and
     * unmuting picks up wherever the slides are rather than restarting. Defaults to false.
     */
    muted?: boolean;
    /**
     * Called on the true/false transition of whether this slideshow is claiming exclusive audio
     * playback (playing with any song segments configured — not only while a specific song's
     * window is active). Callers use this to duck another audio source (e.g. gallery background
     * music) while this slideshow's music plays.
     */
    onAudioActiveChange?: (isActive: boolean) => void;
    /**
     * Fades this slideshow's music down to silence over this many milliseconds when the player is
     * torn down, instead of cutting off instantly. See `useSlideshowAudio`.
     */
    audioFadeOutOnTeardownMs?: number;
    /**
     * Slide id whose Fill/Ken Burns crop should render as plain letterboxed `contain` — the same
     * view the `KenBurnsViewportOverlay` draws on top of — regardless of playback progress.
     * Set by the editor to the selected slide while its crop viewport is being edited (paused).
     */
    freezeKenBurnsForSlideId?: number | null;
}

const SlideshowPlayer: React.FC<ISlideshowPlayerProps> = ({
    slideshow,
    currentTime,
    isPlaying = false,
    onSlideIndexChange,
    watermarkUrl,
    isBuffering,
    loadingIndicator,
    getSongStreamUrl,
    volume = 1,
    muted = false,
    onAudioActiveChange,
    audioFadeOutOnTeardownMs = 0,
    freezeKenBurnsForSlideId = null
}) => {
    const t = useTranslate('studio.common.slideshowPlayer');
    const lastHighlightRef = useRef<number>(0);
    const { slides } = slideshow;

    useSlideshowAudio({
        segments: slideshow.songSegments,
        isPlaying,
        currentTimeMs: currentTime,
        volume,
        muted,
        getStreamUrl: getSongStreamUrl ?? NO_OP_GET_SONG_STREAM_URL,
        onActiveChange: onAudioActiveChange,
        fadeOutOnTeardownMs: audioFadeOutOnTeardownMs
    });

    const segmentDurations = useMemo(() => slides.map(getSlideDurationMs), [slides]);
    const effectiveOverlap = useMemo(() => getBoundaryTransitionDurations(slideshow), [slideshow]);

    const layout = useMemo(
        () =>
            buildOverlapTimeline(segmentDurations, effectiveOverlap, {
                loop: slideshow.loopToFitMusic
            }),
        [segmentDurations, effectiveOverlap, slideshow.loopToFitMusic]
    );

    const playbackDurationMs = useMemo(
        () => getSlideshowPlaybackDurationMs(layout.totalDuration, slideshow.songSegments),
        [layout.totalDuration, slideshow.songSegments]
    );

    const resolvedTimeMs = useMemo(
        () => resolvePlaybackTime(currentTime, layout, playbackDurationMs),
        [currentTime, layout, playbackDurationMs]
    );

    const active = useMemo(
        () => getActiveSlidesAtTime(resolvedTimeMs, layout, playbackDurationMs),
        [resolvedTimeMs, layout, playbackDurationMs]
    );

    // When the playhead is past the last slide (soundtrack longer than slides), show an empty
    // frame using the last slide's background. Use resolved time so Continuous Loop wrap-around
    // is not mistaken for the music-only region.
    const isPastSlideContent = layout.totalDuration > 0 && resolvedTimeMs > layout.totalDuration;

    const highlightedIndex =
        active.secondaryIndex !== null && active.blend > 0.5
            ? active.secondaryIndex
            : active.primaryIndex;

    useEffect(() => {
        if (highlightedIndex !== lastHighlightRef.current) {
            lastHighlightRef.current = highlightedIndex;
            onSlideIndexChange?.(highlightedIndex);
        }
    }, [highlightedIndex, onSlideIndexChange]);

    const slideshowAspectRatio = slideshow.aspectRatio ?? SLIDESHOW_DEFAULTS.aspectRatio;
    const [aspectRatioW, aspectRatioH] = slideshowAspectRatio.split(':').map(Number);
    const cssAspectRatio = `${aspectRatioW} / ${aspectRatioH}`;

    // Fit/Fill/Ken Burns crop rendering needs the slide box's own rendered pixel size (it's
    // letterboxed within a possibly larger/smaller outer container) and each photo's natural
    // pixel size (loaded async — item metadata alone isn't reliable enough to compute a
    // fill-the-box zoom/pan transform).
    const slideBoxRef = useRef<HTMLDivElement | null>(null);
    const [slideBoxSize, setSlideBoxSize] = useState<{ width: number; height: number } | null>(
        null
    );
    const handleSlideBoxResize = useCallback((element: HTMLElement) => {
        setSlideBoxSize({ width: element.clientWidth, height: element.clientHeight });
    }, []);

    useResizeObserver({ containerRef: slideBoxRef, callback: handleSlideBoxResize });

    const [imageNaturalSizes, setImageNaturalSizes] = useState<Record<number, NaturalImageSize>>(
        {}
    );
    const handleImageLoad = useCallback(
        (slideId: number, event: React.SyntheticEvent<HTMLImageElement>) => {
            const img = event.currentTarget;

            if (!img.naturalWidth || !img.naturalHeight) {
                return;
            }

            setImageNaturalSizes((prev) => {
                const existing = prev[slideId];

                if (
                    existing &&
                    existing.width === img.naturalWidth &&
                    existing.height === img.naturalHeight
                ) {
                    return prev;
                }

                return {
                    ...prev,
                    [slideId]: { width: img.naturalWidth, height: img.naturalHeight }
                };
            });
        },
        []
    );

    const blendIn = active.secondaryIndex !== null ? active.blend : 1;
    const blendOut = active.secondaryIndex !== null ? 1 - active.blend : 1;
    const b = active.secondaryIndex !== null ? active.blend : 0;
    const activeTransitionType =
        active.secondaryIndex !== null
            ? getSlideTransition(slides[active.primaryIndex]).type
            : SLIDESHOW_DEFAULTS.slide.transition;
    const isTransitioning = active.secondaryIndex !== null && activeTransitionType !== 'cut';

    const renderSlideLayer = (
        slideIndex: number,
        opacity: number,
        layer: 'incoming' | 'outgoing'
    ) => {
        if (opacity <= 0.001 || slideIndex < 0 || slideIndex >= slides.length) {
            return null;
        }

        const slide = slides[slideIndex];
        const slideImageUrl = getSlideImageUrl(slide);
        const slideBg = getSlideBackgroundColor(slide);
        const placeholderColor = slideBg === '#ffffff' ? 'black' : 'white';

        let transform = 'none';

        if (isTransitioning && activeTransitionType !== 'fade') {
            if (activeTransitionType === 'slide') {
                const shift = layer === 'outgoing' ? -b * 100 : (1 - b) * 100;
                transform = `translateX(${shift}%)`;
            } else if (activeTransitionType === 'zoom') {
                const scale = layer === 'outgoing' ? 1 + b * 0.15 : 0.85 + b * 0.15;
                transform = `scale(${scale})`;
            }
        }

        // Per-layer slide-local progress: `renderSlideLayer` is called separately for the
        // outgoing and incoming slides during a transition, each with its own slideIndex — so
        // the outgoing slide's Ken Burns animation keeps progressing toward its end keyframe
        // while the incoming slide's animation starts fresh, both visible during the crossfade.
        const cropMode = slide.cropMode ?? 'fit';
        let imageStyle: React.CSSProperties = {
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain'
        };
        let cropTransform: string | undefined;
        let willChangeTransform = false;

        if (slideImageUrl && cropMode !== 'fit') {
            const naturalSize = imageNaturalSizes[slide.id];
            const isFrozenForEdit = freezeKenBurnsForSlideId === slide.id;

            if (cropMode === 'fill') {
                const keyframe = slide.cropData?.keyframes[0];

                if (!keyframe) {
                    // Legacy fallback: fill with no stored crop data behaves like the old
                    // always-cover rendering.
                    imageStyle = { width: '100%', height: '100%', objectFit: 'cover' };
                } else if (naturalSize && slideBoxSize) {
                    const playback = getViewportCropPlayback(
                        keyframe,
                        slideBoxSize.width,
                        slideBoxSize.height,
                        naturalSize.width,
                        naturalSize.height,
                        isFrozenForEdit
                    );

                    imageStyle = playback.imageStyle;
                    cropTransform = playback.cropTransform;
                    willChangeTransform = Boolean(playback.willChangeTransform);
                }
            } else if (cropMode === 'ken_burns') {
                const keyframes = slide.cropData?.keyframes ?? [];

                if (keyframes.length >= 2 && naturalSize && slideBoxSize) {
                    const slideStartMs = layout.segmentStarts[slideIndex] ?? 0;
                    const slideDurationMs = layout.segmentDurations[slideIndex] ?? 0;
                    const slideLocalProgress = getSlideLocalProgress(
                        currentTime,
                        slideStartMs,
                        slideDurationMs
                    );
                    const viewport = interpolateKeyframes(
                        keyframes,
                        slideLocalProgress,
                        slide.cropData?.easing ?? 'linear'
                    );
                    const playback = getViewportCropPlayback(
                        viewport,
                        slideBoxSize.width,
                        slideBoxSize.height,
                        naturalSize.width,
                        naturalSize.height,
                        isFrozenForEdit,
                        true
                    );

                    imageStyle = playback.imageStyle;
                    cropTransform = playback.cropTransform;
                    willChangeTransform = Boolean(playback.willChangeTransform);
                }
            }
        }

        // `will-change` promotes the layer to its own compositing surface, which is what keeps a
        // transition's per-frame opacity/transform writes off the main thread. It's derived from
        // the slide's transition config rather than from whether a transition happens to be
        // running, so the value stays constant for the layer's whole life — toggling it as a
        // transition starts would re-promote the layer at the exact moment Safari needs to be
        // doing less work.
        //
        // A slide whose boundaries are all hard cuts gets no hint at all. Promoting it bought
        // nothing (there's nothing to animate) and cost a fresh layer rasterization that Safari
        // landed a frame late, showing as a flash of the slide's background color between slides.
        const animatesIn =
            slides.length > 1 &&
            getSlideTransition(slides[(slideIndex - 1 + slides.length) % slides.length]).type !==
                'cut';
        const animatesOut = getSlideTransition(slide).type !== 'cut';

        return (
            // Plain `<div>`s with inline styles rather than Chakra `<Box>`es, all the way down
            // to the `<img>`. `opacity` and `transform` change on every animation frame during
            // a transition, and every distinct value handed to a Chakra style prop makes
            // Emotion serialize and inject another CSS rule — ~60 new rules per second per
            // layer, retained for the life of the page. That unbounded stylesheet growth is
            // what made Safari playback stutter worst around transitions (FOR-16442). Inline
            // styles write straight to the element instead, so per-frame updates cost nothing
            // beyond the composite.
            <div
                key={`slide-${slideIndex}`}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: cropTransform ? 'hidden' : 'visible',
                    pointerEvents: 'none',
                    opacity,
                    transform,
                    willChange: animatesIn || animatesOut ? 'opacity, transform' : undefined
                }}
            >
                {slideImageUrl ? (
                    cropTransform ? (
                        // Clip layer + block frame. Layout size stays at the contain-drawn
                        // photo size; zoom/pan is a GPU-composited transform (not animated
                        // left/top/width/height). The `<img>` stretches to fill the frame.
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                overflow: 'hidden'
                            }}
                        >
                            <div
                                style={{
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    width: imageStyle.width,
                                    height: imageStyle.height,
                                    maxWidth: 'none',
                                    maxHeight: 'none',
                                    transform: cropTransform,
                                    transformOrigin: '0 0',
                                    willChange: willChangeTransform ? 'transform' : undefined
                                }}
                            >
                                <img
                                    src={slideImageUrl}
                                    alt={t('slideAltText', { index: slideIndex + 1 })}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'fill',
                                        display: 'block'
                                    }}
                                    onLoad={(event) => handleImageLoad(slide.id, event)}
                                />
                            </div>
                        </div>
                    ) : (
                        <img
                            src={slideImageUrl}
                            alt={t('slideAltText', { index: slideIndex + 1 })}
                            // No `decoding="async"`: it lets the browser present the element
                            // before the bitmap is ready, and at a hard cut the layer appears at
                            // full opacity with no crossfade to hide an undecoded frame — which
                            // read as a flash of the slide background in Safari. The preloader
                            // already decodes each slide via `img.decode()` before playback, so
                            // the hint bought nothing here anyway.
                            style={imageStyle}
                            onLoad={(event) => handleImageLoad(slide.id, event)}
                        />
                    )
                ) : (
                    <Box
                        fontSize="sm"
                        color={placeholderColor}
                        opacity={0.7}
                        textAlign="center"
                        px={4}
                    >
                        {t('photoDeleted')}
                    </Box>
                )}
            </div>
        );
    };

    const outgoingIdx = active.primaryIndex;
    const incomingIdx = active.secondaryIndex;
    const lastSlideIndex = Math.max(0, slides.length - 1);
    const backgroundColor = getSlideBackgroundColor(
        slides[isPastSlideContent ? lastSlideIndex : highlightedIndex]
    );

    return (
        <Box
            flex={1}
            minH={0}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            overflow="hidden"
            sx={{ containerType: 'size' }}
        >
            <Box
                ref={slideBoxRef}
                position="relative"
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                // Inline, not Chakra's `bg`: this tracks `highlightedIndex`, which flips at the
                // midpoint of every crossfade, so a style prop here handed Emotion a new rule to
                // serialize in the middle of each transition (FOR-16442).
                style={{
                    backgroundColor:
                        slides.length === 0
                            ? SLIDESHOW_DEFAULTS.slide.backgroundColor
                            : backgroundColor
                }}
                color="white"
                width={`min(100cqw, calc(100cqh * ${aspectRatioW} / ${aspectRatioH}))`}
                height={`min(100cqh, calc(100cqw * ${aspectRatioH} / ${aspectRatioW}))`}
                maxW="100%"
                maxH="100%"
                aspectRatio={cssAspectRatio}
                data-testid={isPastSlideContent ? 'slideshow-player-past-slides' : undefined}
            >
                {slides.length === 0 ? (
                    t('noSlides')
                ) : isPastSlideContent ? null : incomingIdx === null ? (
                    renderSlideLayer(outgoingIdx, 1, 'outgoing')
                ) : activeTransitionType === 'cut' ? (
                    renderSlideLayer(incomingIdx, 1, 'incoming')
                ) : (
                    <>
                        {renderSlideLayer(outgoingIdx, blendOut, 'outgoing')}
                        {renderSlideLayer(incomingIdx, blendIn, 'incoming')}
                    </>
                )}
                {watermarkUrl && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        pointerEvents="none"
                    >
                        <img
                            src={watermarkUrl}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'fill' }}
                        />
                    </Box>
                )}
                {isBuffering && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        backgroundColor="rgba(0, 0, 0, 0.75)"
                        zIndex={1000}
                        pointerEvents="none"
                    >
                        {loadingIndicator}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SlideshowPlayer;
